import React from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { useNavigate } from 'react-router-dom';
import SubscriptionDialog, { DialogButton } from './SubscriptionDialog';
import styles from './PlanUpgradeModal.module.css';
import { getWhatsAppSupportLink } from '../../consts/ContactConfig';
import { BUSINESS_PLAN_NAME, LIMIT_UPGRADE_HINT, planAvailabilityText, planLimitText } from './planLockTexts';
import useCurrentPlanLabel from './useCurrentPlanLabel';

const BENEFITS = [
  'Limites ampliados ou ilimitados',
  'Acesso a funcionalidades exclusivas',
  'Mais recursos para crescer seu negócio',
];

/**
 * Modal exibido quando o usuário clica em "Fazer upgrade" a partir de um
 * FeatureLockBanner. Direciona para a página de planos.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla a visibilidade do modal
 * @param {Function} props.onClose - Callback para fechar o modal
 * @param {string} props.featureName - Nome da feature que motivou o upgrade
 * @param {'limit'|'permission'} props.type - Tipo do aviso
 * @param {string} [props.planName] - Plano que libera a feature. Com "Business"
 *   o atalho é "Falar com a gente" (WhatsApp da Gabriela), como em Plans.js;
 *   nos demais, a página de planos.
 * @param {boolean} [props.plural] - featureName no plural ("estão disponíveis").
 * @param {number} [props.limit] - Limite do plano (type='limit'): com ele o texto
 *   é o mesmo do FeatureLockBanner ("O plano Essencial permite até 1 agenda").
 * @param {string} [props.featureNameSingular] - Item no singular (type='limit').
 * @param {string} [props.hint] - O que fazer no limite (type='limit'), de
 *   limitActionHint() (ex.: reativar); sem ele, "Mude de plano para adicionar mais."
 */
const PlanUpgradeModal = ({
  isOpen,
  onClose,
  featureName,
  type,
  planName,
  plural = false,
  limit,
  featureNameSingular,
  hint = LIMIT_UPGRADE_HINT
}) => {
  const navigate = useNavigate();
  const planLabel = useCurrentPlanLabel();
  const isBusiness = planName === BUSINESS_PLAN_NAME;

  const handleViewPlans = () => {
    onClose();
    if (isBusiness) {
      const text = 'Olá! Tenho interesse no plano Business da Gabriela e gostaria de saber mais.';
      window.open(getWhatsAppSupportLink(text), '_blank', 'noopener');
      return;
    }
    navigate('/subscription/plans');
  };

  const getDescription = () => {
    if (type === 'limit' && typeof limit === 'number') {
      return `${planLimitText(planLabel, limit, featureNameSingular || featureName, featureName)}. ${hint}`;
    }
    if (type === 'limit') {
      return (
        <>
          Você atingiu o limite de <strong>{featureName}</strong> disponível no seu plano atual.
          Faça upgrade para continuar crescendo sem restrições.
        </>
      );
    }
    if (planName) {
      return (
        <>
          <strong>{featureName}</strong> {planAvailabilityText(planName, plural)}.
          {isBusiness
            ? ' Fale com a gente para conhecer o plano e ativar esse e outros recursos.'
            : ' Mude de plano para desbloquear esse e outros recursos avançados.'}
        </>
      );
    }
    return (
      <>
        <strong>{featureName}</strong> é uma funcionalidade exclusiva de planos superiores.
        Faça upgrade para desbloquear esse e outros recursos avançados.
      </>
    );
  };

  const Icon = type === 'limit' ? TrendingUpRoundedIcon : LockOutlinedIcon;
  const showFeatureCard = type !== 'limit' && featureName;

  const getTitle = () => {
    if (isBusiness) return 'Conheça o plano Business';
    if (type === 'limit') return 'Limite do plano atingido';
    return planName ? `Disponível a partir do plano ${planName}` : 'Recurso de outro plano';
  };

  return (
    <SubscriptionDialog
      open={isOpen}
      onClose={onClose}
      maxWidth={480}
      icon={<span className={styles.iconWrapper}><Icon fontSize="inherit" /></span>}
      title={getTitle()}
      subtitle={getDescription()}
      secondaryAction={<DialogButton variant="text" onClick={onClose}>Agora não</DialogButton>}
      actions={(
        <DialogButton onClick={handleViewPlans}>
          {isBusiness ? 'Falar com a gente' : 'Ver planos'}
        </DialogButton>
      )}
    >
      {showFeatureCard && (
        <div className={styles.featureCard}>
          <span className={styles.featureName}>{featureName}</span>
          {planName && <span className={styles.planPill}>Plano {planName}</span>}
        </div>
      )}
      <div className={styles.benefits}>
        <span className={styles.benefitsTitle}>Com um plano superior você tem</span>
        <ul className={styles.benefitsList}>
          {BENEFITS.map((benefit) => (
            <li key={benefit} className={styles.benefitItem}>
              <span className={styles.benefitIcon}><CheckRoundedIcon fontSize="inherit" /></span>
              {benefit}
            </li>
          ))}
        </ul>
      </div>
    </SubscriptionDialog>
  );
};

export default PlanUpgradeModal;
